import { useEffect, useRef } from "react";

/**
 * App-wide keyboard registry (UIUX §5.1). One document-level keydown
 * listener with a scope stack — no dependency, mirroring GlobalSearch's
 * hand-rolled pattern. Components register their bindings with
 * `useHotkeys`; `useHotkeysRoot` (mounted once in AppShell) dispatches.
 *
 * Rules encoded here, not per-caller:
 * - single-letter keys are ignored while focus is in an
 *   input/textarea/select/contenteditable (Cmd-combos are not this
 *   registry's business — they stay on their existing local handlers);
 * - later registrations shadow earlier ones for the same key (the thread
 *   pane mounts after the list, so `j` means next-lead there);
 * - two-key sequences ("a a" = assign to me) fire when the second key
 *   lands within the sequence window; the single-key binding still fires
 *   on the first press (open popover → refine to me);
 * - while a dialog/popover is open only Escape and in-flight sequences
 *   dispatch, so a stray `e` can't close a lead under a dialog;
 * - the `?` help overlay renders straight from this registry, so the
 *   cheat-sheet can't drift from the real bindings.
 */
export interface Hotkey {
  /**
   * `KeyboardEvent.key` value ("j", "E", "?", "Enter", "Escape") or a
   * two-key sequence ("a a"). Case-sensitive: "E" means Shift+e.
   */
  keys: string;
  /** Help-overlay grouping label ("Inbox list", "Thread", "Global"). */
  scope: string;
  /** Help-overlay action description. */
  description: string;
  /** Return false to pass the key to the next (earlier) registration. */
  onTrigger: (e: KeyboardEvent) => void | boolean;
  /** When false the binding is skipped entirely. */
  enabled?: boolean;
}

interface Registration {
  id: number;
  get: () => Hotkey[];
}

const SEQUENCE_WINDOW_MS = 600;

let nextId = 1;
const registrations: Registration[] = [];
const subscribers = new Set<() => void>();

function notify() {
  for (const cb of subscribers) cb();
}

/** Snapshot of every registered binding, registration order. */
export function getHotkeys(): Hotkey[] {
  return registrations.flatMap((r) => r.get());
}

/** Subscribe to registry mount/unmount changes (help overlay). */
export function subscribeHotkeys(cb: () => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

/**
 * Register a component's bindings for its lifetime. The bindings array
 * is re-read on every dispatch (via ref), so handlers and `enabled`
 * always see fresh state without re-registering.
 */
export function useHotkeys(hotkeys: Hotkey[]): void {
  const ref = useRef(hotkeys);
  ref.current = hotkeys;
  useEffect(() => {
    const registration: Registration = { id: nextId++, get: () => ref.current };
    registrations.push(registration);
    notify();
    return () => {
      const idx = registrations.indexOf(registration);
      if (idx >= 0) registrations.splice(idx, 1);
      notify();
    };
  }, []);
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(
    target.closest("input, textarea, select, [contenteditable='true'], [contenteditable='']"),
  );
}

function isOverlayOpen(): boolean {
  return Boolean(
    document.querySelector(
      "[role='dialog'][data-state='open'], [data-radix-popper-content-wrapper]",
    ),
  );
}

/** Latest-registered enabled binding for `keys`, or null. */
function findBinding(keys: string): Hotkey | null {
  for (let i = registrations.length - 1; i >= 0; i--) {
    for (const hk of registrations[i]!.get()) {
      if (hk.keys === keys && hk.enabled !== false) return hk;
    }
  }
  return null;
}

function fire(keys: string, e: KeyboardEvent): boolean {
  for (let i = registrations.length - 1; i >= 0; i--) {
    for (const hk of registrations[i]!.get()) {
      if (hk.keys !== keys || hk.enabled === false) continue;
      if (hk.onTrigger(e) === false) continue; // pass to next registration
      e.preventDefault();
      return true;
    }
  }
  return false;
}

/**
 * Install the single document keydown dispatcher. Mounted exactly once
 * (AppShell); must be mounted exactly once (AppShell) — a second mount would double-fire every binding.
 */
export function useHotkeysRoot(): void {
  useEffect(() => {
    let pendingKey: string | null = null;
    let pendingAt = 0;

    function onKeyDown(e: KeyboardEvent) {
      if (e.defaultPrevented) return;
      // Modifier combos (Cmd+K, Cmd+Enter…) belong to their own
      // handlers; Shift stays legal — "?" and "E" need it.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key;
      if (key === "Shift") return;

      if (isEditableTarget(e.target)) {
        // Typing. Only Escape penetrates (blur/clear semantics).
        pendingKey = null;
        if (key === "Escape") fire("Escape", e);
        return;
      }

      // Two-key sequence completion beats everything — the first key may
      // have opened a popover (e.g. `a` → assign, `a` again → me).
      if (pendingKey && Date.now() - pendingAt <= SEQUENCE_WINDOW_MS) {
        const seq = `${pendingKey} ${key}`;
        if (findBinding(seq)) {
          pendingKey = null;
          fire(seq, e);
          return;
        }
      }

      if (isOverlayOpen()) {
        // A dialog owns the keyboard; Radix handles its own Escape.
        pendingKey = null;
        return;
      }

      const fired = fire(key, e);
      // Remember the key (fired or not) so sequences like `a a` work
      // whether or not the single `a` had a binding in this scope.
      pendingKey = key;
      pendingAt = Date.now();
      void fired;
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);
}
